import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeFoldersPaginatedService } from '@app/consignee/services/consignee-folders-paginated.service';
import { DeliveryNoteBundlesService } from 'api/services';
import { AppStore } from '@app/stores/appStore';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { lastValueFrom } from 'rxjs';
import { ColumnCreator, ColumnValue } from '../column-creator';
import { FilterKeyCreator } from '../filter-key-creator';
import {
    FolderData,
    DeliveryNoteData,
    TableRowData,
    hasKey,
    hasStatus,
    DeliveryNote,
    hasReadOnly,
} from '@app/shared/models/table-data-types';
import { SelectionStore, filterFolderData } from '@app/stores/selectionStore';
import { hasBundleGrouping } from '@app/core/stores/permissionsStore';

export interface ConsigneeFoldersConfigDeps {
    consigneeFoldersPaginatedService: ConsigneeFoldersPaginatedService;
    deliveryNoteBundlesService: DeliveryNoteBundlesService;
    actionsService: ConsigneeViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    selectionStore: InstanceType<typeof SelectionStore>;
}

export class ConsigneeFoldersConfig {
    static create(deps: ConsigneeFoldersConfigDeps): ViewConfig {
        return {
            id: 'consignee-folders',
            name: 'Folders',
            translationKey: 'OUTGOING-GOODS.TOGGLER.FOLDERS',
            type: 'folders',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consigneeFoldersPaginatedService.loadConsigneeFoldersPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isNotClosed =
                                hasStatus(item) && item.status !== 'closed';
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isNotClosed && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.details(),
                    ColumnCreator.bundleGroupMembership(),
                    ColumnCreator.bundleNumber(),
                    ColumnCreator.bundleName(),
                    ColumnCreator.actions({
                        width: '200px',
                        configuration: {
                            type: 'bundle',
                            showDownload: true,
                            showViewPdf: true,
                            showDeliveryNotesCount: true,
                            downloadEndpoint: (item: TableRowData) =>
                                deps.actionsService.downloadDeliveryBundle(
                                    item as FolderData
                                ),
                            viewPdfEndpoint: (item: TableRowData) =>
                                deps.actionsService.viewFolderAsPdf(
                                    item as FolderData
                                ),
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.consignorName({
                        header: 'INCOMING-GOODS.TABLE.CONSIGNOR-NAME',
                    }),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                    }),
                    ColumnCreator.consigneeName(),
                    ColumnCreator.dateShipped({
                        header: 'INCOMING-GOODS.TABLE.DATE',
                        filterable: true,
                    }),
                    ColumnCreator.status({
                        header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                        showDiscrepanciesIcon: true,
                    }),
                    ColumnCreator.qsStatus({
                        header: 'INCOMING-GOODS.TABLE.QS_STATUS',
                        sortable: false,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: FolderData) =>
                        item.numberOfDeliveryNotes > 0,
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '200px',
                                configuration: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: TableRowData
                                ) =>
                                    ({
                                        type: 'deliveryNote',
                                        showView: true,
                                        showDownload: true,
                                        showDeliveryNotesCount: false,
                                        showAddNotes: true,
                                        downloadEndpoint: (
                                            deliveryNote: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            return deps.actionsService.downloadDeliveryNote(
                                                deliveryNote as DeliveryNoteData,
                                                parentRow as FolderData
                                            );
                                        },
                                        viewEndpoint: (
                                            deliveryNote: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            return deps.actionsService.viewDeliveryNote(
                                                deliveryNote as DeliveryNoteData,
                                                parentRow as FolderData
                                            );
                                        },
                                        addNotesEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.addFineDiscrepancy(
                                                item as DeliveryNoteData,
                                                parentRow as FolderData
                                            ),
                                        bundleKey:
                                            parentRow && hasKey(parentRow)
                                                ? parentRow._key
                                                : undefined,
                                        bundleStatus:
                                            parentRow && hasStatus(parentRow)
                                                ? parentRow.status
                                                : undefined,
                                        organizationSiteKey:
                                            parentRow?.orgSiteKey,
                                        context: 'consignee',
                                    }) as TableActionsConfig,
                            }),
                            ColumnCreator.deliveryNoteNumber(),
                            ColumnCreator.orderNumber(),
                            ColumnCreator.referenceNumber({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                                orgType: 'consignee',
                                readOnly: true,
                            }),
                            ColumnCreator.dateAnnounced({
                                header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                            }),
                            ColumnCreator.dateReceived({
                                header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                            }),
                            ColumnCreator.status({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                            }),
                            ColumnCreator.discrepancies(),
                            ColumnCreator.palletTickets(),
                            ColumnCreator.originalConsignor(),
                            ColumnCreator.originalConsignorGln(),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: FolderData,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                const result =
                                    deps.deliveryNoteBundlesService.getConsigneeBundle(
                                        {
                                            deliveryNoteBundleKey:
                                                parentRow._key,
                                            organizationSiteKey:
                                                organizationSiteKey,
                                        }
                                    );
                                const bundle =
                                    result instanceof Promise
                                        ? await result
                                        : await lastValueFrom(result);

                                // Transform delivery notes to include required properties
                                return (bundle.deliveryNotes || []).map(
                                    (deliveryNote: DeliveryNote) => ({
                                        ...deliveryNote,
                                        orgSiteKey: parentRow.consigneeKey,
                                        read_only:
                                            parentRow.status === 'closed' ||
                                            parentRow.read_only ||
                                            false,
                                        status:
                                            deliveryNote.dateReceived &&
                                            deliveryNote.dateReceived > 0
                                                ? 'closed'
                                                : bundle.status,
                                    })
                                );
                            },
                            organizationSiteKeyResolver: (row: FolderData) =>
                                row.consigneeKey,
                            customParams: (row: FolderData) => ({
                                bundleKey: row._key,
                                bundleStatus: row.status,
                            }),
                        },
                        loadingMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.LOADING-DELIVERY-NOTES',
                        emptyMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.NO-DELIVERY-NOTES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    FilterKeyCreator.text(
                        'bundleNumber',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO'
                    ),
                    FilterKeyCreator.text(
                        'name',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME'
                    ),
                    FilterKeyCreator.text(
                        'consignorName',
                        'INCOMING-GOODS.TABLE.CONSIGNOR-NAME'
                    ),
                    FilterKeyCreator.text(
                        'carrierName',
                        'INCOMING-GOODS.TABLE.CARRIER-NAME'
                    ),
                    FilterKeyCreator.text(
                        'consigneeName',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNEE-NAME'
                    ),
                    FilterKeyCreator.date(
                        'checkoutDate',
                        'INCOMING-GOODS.TABLE.DATE'
                    ),
                    FilterKeyCreator.status(
                        'status',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE'
                    ),
                ],
                showClosed: false,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'name',
                    'bundleNumber',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'checkoutDate',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: true,
                canDelete: false,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'checkinDeliveryNoteBundles',
                        label: 'Check-in Folders',
                        translationKey: 'INCOMING-GOODS.BUTTONS.CHECKIN-FOLDER',
                        svgIcon: 'folder-checkout',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => {
                            if (!selection || selection.length === 0) {
                                return true;
                            }
                            // Bundles of a bundle group (Mappengruppe) may only
                            // be checked in via their group, so a single grouped
                            // bundle in the selection blocks the whole check-in.
                            return deps.actionsService.hasGroupedBundles(
                                filterFolderData(
                                    deps.selectionStore.selectedItems()
                                )
                            );
                        },
                        handler: async () => {
                            const currentSelection =
                                deps.selectionStore.selectedItems();
                            const isValid =
                                deps.selectionStore.isSelectionValidForCheckin();
                            const readyCount =
                                deps.selectionStore.checkinReadyCount();

                            if (isValid && readyCount > 0) {
                                // Filter to only folder data items
                                const folderItems =
                                    filterFolderData(currentSelection);
                                if (folderItems.length > 0) {
                                    await deps.actionsService.checkinDeliveryNoteBundles(
                                        folderItems
                                    );
                                } else {
                                }
                            } else {
                            }
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.CHECKIN-FOLDER',
                    },
                    {
                        id: 'scanWallet',
                        label: 'Scan Wallet',
                        translationKey: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        // Der Wallet-Scan startet einen Check-in. Fernzugriff
                        // ist nur lesend — das Backend vergibt der Site dann
                        // keine consignee_checkins_create-Berechtigung.
                        disabled: (item, selection) =>
                            !deps.appStore.currentSiteRole()?.siteInfo
                                ?.permissions?.consignee_checkins_create,
                        handler: async () => {
                            deps.actionsService.scanWallet();
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                    },
                    {
                        id: 'groupBundles',
                        label: 'INCOMING-GOODS.BUTTONS.GROUP-BUNDLES',
                        translationKey: 'INCOMING-GOODS.BUTTONS.GROUP-BUNDLES',
                        icon: 'inventory_2',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 3,
                        visible: () => hasBundleGrouping(),
                        disabled: (item, selection) => {
                            const currentSelection =
                                deps.selectionStore.selectedItems();
                            const folderItems =
                                filterFolderData(currentSelection);
                            return !deps.actionsService.canGroupBundles(
                                folderItems
                            );
                        },
                        handler: async () => {
                            const folderItems = filterFolderData(
                                deps.selectionStore.selectedItems()
                            );
                            await deps.actionsService.groupBundles(folderItems);
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.GROUP-BUNDLES',
                    },
                ],
            },
        };
    }
}
